// frontend/src/pages/EnterprisePage/EnterprisePage.jsx

import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Paper, Tabs, Tab, Button } from '@mui/material';

// Icons
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GavelIcon from '@mui/icons-material/Gavel';
import CropIcon from '@mui/icons-material/Crop';
import MobileOffIcon from '@mui/icons-material/MobileOff';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ApiIcon from '@mui/icons-material/Api';
import ShieldIcon from '@mui/icons-material/Shield';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// New Icons for the Threat Radar Funnel
import RadarIcon from '@mui/icons-material/Radar';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { useTranslation } from 'react-i18next';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import './EnterprisePage.css';

// --- ANIMATION VARIANTS ---

// 1. Letter-by-letter title animations (Matches Landing Page)
const sentenceVariants = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: { delay: 0.2, staggerChildren: 0.04 },
    },
};

const letterVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', damping: 12, stiffness: 200 },
    },
};

// Subtitle fade-in (Delayed until title finishes typing)
const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
        opacity: 1, 
        y: 0, 
        transition: { duration: 0.8, delay: 1.5, ease: "easeOut" } 
    }
};

// Arrow fade-in & bounce (Delayed until everything else is shown)
const arrowVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        y: [0, 15, 0],
        transition: {
            opacity: { duration: 1, delay: 2.2 },
            y: { repeat: Infinity, duration: 2, ease: "easeInOut", delay: 2.2 }
        }
    }
};

// Slower sequential fading for grids (Pillars & Funnel)
const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.35 } // Slower delay between each pillar appearing
    }
};

const fadeLeftToRight = {
    hidden: { opacity: 0, x: -10 }, // Reduced from -30 to prevent flex element overlap during animation
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
};


// Configuration for mapping icons to the translation dictionary IDs
const useCasesConfig = [
    { id: 'ipProtection', icon: <ShieldIcon />, visualIcon: <CropIcon className="ent-abstract-icon" /> },
    { id: 'mediaIntegrity', icon: <LanguageIcon />, visualIcon: <GavelIcon className="ent-abstract-icon" /> },
    { id: 'crisisManagement', icon: <BusinessCenterIcon />, visualIcon: <VisibilityOffIcon className="ent-abstract-icon" /> },
    { id: 'api', icon: <ApiIcon />, visualIcon: <ApiIcon className="ent-abstract-icon" /> }
];

// Funnel Steps Configuration (No hardcoded English text)
const funnelStepsConfig = [
    { icon: <RadarIcon fontSize="large" />, key: 'step1' },
    { icon: <FilterAltIcon fontSize="large" />, key: 'step2' },
    { icon: <FingerprintIcon fontSize="large" />, key: 'step3', isOptional: true },
    { icon: <PsychologyIcon fontSize="large" />, key: 'step4' },
    { icon: <GavelIcon fontSize="large" />, key: 'step5' }
];

// Shared Tab Styles
const tabStyles = { 
    minHeight: '36px', 
    py: 0.5, px: 2, 
    borderRadius: '20px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.6)', 
    zIndex: 1,
    '&.Mui-selected': { 
        color: 'white', 
        background: 'rgba(255,255,255,0.15)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
    },
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
};

const EnterprisePage = () => {
    const { t } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const containerRef = useRef(null);

    const headlineText = t('enterprisePage.hero.headline', 'The Invisible Layer of Digital Trust');

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.scrollTop = 0;
        }
    }, []);

    const handleScroll = (e) => {
        setIsScrolled(e.target.scrollTop > 20);
    };

    const scrollToSection = (index) => {
        const container = containerRef.current;
        const sections = document.querySelectorAll('.ent-snap-section');
        if (container && sections[index]) {
            container.scrollTo({ top: sections[index].offsetTop, behavior: 'smooth' });
        }
    };

    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true } },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.3, width: 1 },
            move: { enable: true, speed: 0.8 },
            number: { density: { enable: true, area: 800 }, value: 60 },
            opacity: { value: 0.4 },
            size: { value: { min: 1, max: 2 } },
        },
    };

    return (
        <div ref={containerRef} onScroll={handleScroll} className={`ent-page-container ${isScrolled ? 'scrolled' : ''}`}>
            <Particles id="tsparticles-ent" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('enterprisePage.helmet.title')}</title>
                <meta name="description" content={t('enterprisePage.helmet.description')} />
            </Helmet>
            
            <div className="ent-nav-container">
                <UniversalNav />
            </div>

            <main className="ent-content-wrapper">
                
                {/* --- BLOCK 1: HERO --- */}
                <div className="ent-snap-section">
                    <Box className="ent-header" sx={{ zIndex: 2 }}>
                        {/* Title Animation */}
                        <motion.h1
                            className="ent-headline"
                            variants={sentenceVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {headlineText.split('').map((char, index) => (
                                <motion.span key={char + '-' + index} variants={letterVariants}>
                                    {char}
                                </motion.span>
                            ))}
                        </motion.h1>

                        {/* Subtitle Animation */}
                        <motion.div initial="hidden" animate="visible" variants={subtitleVariants}>
                            <Typography variant="subtitle1" component="p" className="ent-subheadline">
                                {t('enterprisePage.hero.subheadline')}
                            </Typography>
                        </motion.div>
                    </Box>

                    {/* ONLY ONE SCROLL INDICATOR HERE */}
                    <div className="scroll-indicator-wrapper-enterprise">
                        <motion.div 
                            className="scroll-indicator"
                            variants={arrowVariants}
                            initial="hidden"
                            animate="visible"
                            onClick={() => scrollToSection(1)}
                        >
                            <ExpandMoreIcon sx={{ fontSize: '4rem' }} />
                        </motion.div>
                    </div>
                </div>

                {/* --- BLOCK 2: THE PROBLEM & MOAT --- */}
                <div className="ent-snap-section">
                    <Box className="ent-glass-container">
                        <Typography variant="h4" component="h2" align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {t('enterprisePage.gap.title')}
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                            {t('enterprisePage.gap.subtitle')}
                        </Typography>

                        <div className="ent-gap-grid">
                            <Paper className="ent-gap-card">
                                <MobileOffIcon className="ent-gap-icon" />
                                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                                    {t('enterprisePage.gap.cards.metadata.title')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {t('enterprisePage.gap.cards.metadata.desc')}
                                </Typography>
                            </Paper>
                            <Paper className="ent-gap-card">
                                <CropIcon className="ent-gap-icon" />
                                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                                    {t('enterprisePage.gap.cards.manipulation.title')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {t('enterprisePage.gap.cards.manipulation.desc')}
                                </Typography>
                            </Paper>
                            <Paper className="ent-gap-card">
                                <AutoAwesomeIcon className="ent-gap-icon" />
                                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'white', mb: 0.5 }}>
                                    {t('enterprisePage.gap.cards.aesthetic.title')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {t('enterprisePage.gap.cards.aesthetic.desc')}
                                </Typography>
                            </Paper>
                        </div>

                        {/* THE VETRA AI MOAT (Sequential Fade Left-to-Right) */}
                        <Box className="ent-pillar-container">
                            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: 'white' }}>
                                {t('enterprisePage.moat.title')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, maxWidth: '800px', mx: 'auto' }}>
                                {t('enterprisePage.moat.subtitle')}
                            </Typography>
                            
                            <motion.div 
                                className="ent-pillar-grid"
                                variants={staggerContainer}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: false, amount: 0.3 }}
                            >
                                <motion.div variants={fadeLeftToRight} className="ent-pillar-item">
                                    <div className="ent-pillar-icon-wrapper"><VisibilityOffIcon fontSize="medium" /></div>
                                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {t('enterprisePage.moat.pillars.tracker.title')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                                        {t('enterprisePage.moat.pillars.tracker.desc')}
                                    </Typography>
                                </motion.div>

                                <motion.div variants={fadeLeftToRight} className="ent-pillar-item">
                                    <div className="ent-pillar-icon-wrapper">
                                        <Box sx={{
                                            width: 24, height: 24,
                                            backgroundColor: 'currentColor',
                                            maskImage: 'url(/static/previews/badge_template.png)',
                                            WebkitMaskImage: 'url(/static/previews/badge_template.png)',
                                            maskSize: 'contain', WebkitMaskSize: 'contain',
                                            maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat',
                                            maskPosition: 'center', WebkitMaskPosition: 'center'
                                        }} />
                                    </div>
                                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {t('enterprisePage.moat.pillars.engine.title')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                                        {t('enterprisePage.moat.pillars.engine.desc')}
                                    </Typography>
                                </motion.div>

                                <motion.div variants={fadeLeftToRight} className="ent-pillar-item">
                                    <div className="ent-pillar-icon-wrapper"><GavelIcon fontSize="medium" /></div>
                                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                                        {t('enterprisePage.moat.pillars.ledger.title')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                                        {t('enterprisePage.moat.pillars.ledger.desc')}
                                    </Typography>
                                </motion.div>
                            </motion.div>
                        </Box>
                    </Box>
                </div>

                {/* --- BLOCK 3: PROACTIVE THREAT RADAR FUNNEL (The Pitch) --- */}
                <div className="ent-snap-section">
                    <Box className="ent-glass-container">
                        <Typography variant="h4" component="h2" align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {t('enterprisePage.funnel.title', 'Autonomous Threat Radar')}
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ color: 'rgba(255,255,255,0.7)', mb: 4 }}>
                            {t('enterprisePage.funnel.subtitle', 'How we proactively hunt and eliminate brand threats across the internet at scale.')}
                        </Typography>

                        <motion.div 
                            className="ent-funnel-grid"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: false, amount: 0.2 }}
                        >
                            {funnelStepsConfig.map((step, index) => {
                                // Color gradient from Red (danger/start) to Green (safe/resolved)
                                const badgeColors = ['#e53935', '#fb8c00', '#fbc02d', '#7cb342', '#43a047'];

                                return (
                                    <motion.div 
                                        key={step.key}
                                        variants={fadeLeftToRight} 
                                        className="ent-funnel-card"
                                        style={{ '--badge-color': badgeColors[index] }}
                                    >
                                        {/* Colored Number Badge */}
                                        <div className="ent-funnel-badge" style={{ backgroundColor: badgeColors[index] }}>
                                            {index + 1}
                                        </div>

                                        <div className="ent-funnel-icon">{step.icon}</div>
                                        
                                        {/* Fixed height wrapper keeps descriptions aligned if titles wrap to 2 lines */}
                                        <Box sx={{ minHeight: '3.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                                            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                                                {t(`enterprisePage.funnel.steps.${step.key}.title`)}
                                            </Typography>
                                        </Box>
                                        
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>
                                            {t(`enterprisePage.funnel.steps.${step.key}.desc`)}
                                        </Typography>

                                        {/* Arrow absolute-positioned perfectly in the gap between cards */}
                                        {index < funnelStepsConfig.length - 1 && (
                                            <div className="ent-funnel-arrow">
                                                <ArrowForwardIosIcon />
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    </Box>
                </div>

                {/* --- BLOCK 4: USE CASES & CTA --- */}
                <div className="ent-snap-section">
                    <Box className="ent-glass-container">
                        <Typography variant="h4" align="center" sx={{ fontWeight: 600, mb: 2 }}>
                            {t('enterprisePage.useCases.title')}
                        </Typography>

                        {/* Pill-Shaped Switcher */}
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <Tabs 
                                value={activeTab} 
                                onChange={(e, val) => setActiveTab(val)} 
                                TabIndicatorProps={{ style: { display: 'none' } }}
                                sx={{ 
                                    minHeight: '44px',
                                    background: 'rgba(0,0,0,0.2)',
                                    borderRadius: '24px',
                                    padding: '4px',
                                    border: '1px solid rgba(255,255,255,0.08)'
                                }}
                            >
                                {useCasesConfig.map((uc, idx) => (
                                    <Tab 
                                        key={idx} 
                                        icon={uc.icon} 
                                        iconPosition="start" 
                                        label={t(`enterprisePage.useCases.cases.${uc.id}.title`)} 
                                        sx={tabStyles} 
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                                className="ent-split-section"
                            >
                                <div className="ent-split-text">
                                    <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                                        {t(`enterprisePage.useCases.cases.${useCasesConfig[activeTab].id}.subtitle`)}
                                    </Typography>
                                    
                                    <Typography variant="subtitle1" sx={{ color: '#e57373', fontWeight: 'bold', mb: 0.5 }}>
                                        {t('enterprisePage.useCases.labels.problem')}
                                    </Typography>
                                    <Typography paragraph sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
                                        {t(`enterprisePage.useCases.cases.${useCasesConfig[activeTab].id}.problem`)}
                                    </Typography>
                                    
                                    <Typography variant="subtitle1" sx={{ color: '#81c784', fontWeight: 'bold', mb: 0.5 }}>
                                        {t('enterprisePage.useCases.labels.solution')}
                                    </Typography>
                                    <Typography paragraph sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                        {t(`enterprisePage.useCases.cases.${useCasesConfig[activeTab].id}.solution`)}
                                    </Typography>
                                </div>

                                <div className="ent-split-visual">
                                    <div className="ent-abstract-visual">
                                        {useCasesConfig[activeTab].visualIcon}
                                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', zIndex: 2, px: 2 }}>
                                            {t(`enterprisePage.useCases.cases.${useCasesConfig[activeTab].id}.visualText`)}
                                        </Typography>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </Box>
                    
                    {/* CALL TO ACTION */}
                    <Box sx={{ textAlign: 'center', mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                            {t('enterprisePage.cta.title')}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {/* Primary CTA: Contact Sales */}
                            <Button 
                                variant="contained" 
                                startIcon={<MailOutlineIcon />}
                                onClick={() => {
                                    // VETRA_AI_IGNORE_NEXT
                                    const email = "info@vetra-ai.com";
                                    const subject = encodeURIComponent(t('enterprisePage.cta.emailSubject'));
                                    const body = encodeURIComponent(t('enterprisePage.cta.emailBody'));
                                    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
                                }}
                                sx={{ 
                                    backgroundColor: 'var(--secondary-color)', 
                                    color: 'white', 
                                    padding: '10px 30px', 
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    borderRadius: '30px',
                                    textTransform: 'none',
                                    boxShadow: '0 4px 20px rgba(79, 195, 247, 0.3)',
                                    '&:hover': { 
                                        backgroundColor: 'var(--btn-hover-color)',
                                        boxShadow: '0 6px 25px rgba(79, 195, 247, 0.5)'
                                    }
                                }}
                            >
                                {t('enterprisePage.cta.button')}
                            </Button>

                            {/* Secondary CTA: Live Demo on Homepage */}
                            <Button 
                                variant="outlined" 
                                component={RouterLink}
                                to="/"
                                startIcon={<VisibilityOffIcon />}
                                sx={{ 
                                    borderColor: 'rgba(255, 255, 255, 0.4)',
                                    color: 'white !important', 
                                    padding: '10px 24px', 
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: '30px',
                                    textTransform: 'none',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': { 
                                        borderColor: 'white !important',
                                        backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
                                        color: 'white !important', // Prevents global CSS from turning text dark
                                        boxShadow: '0 0 15px rgba(255, 255, 255, 0.2)',
                                    },
                                    '& .MuiButton-startIcon': {
                                        color: 'white !important' // Ensures the icon stays white as well
                                    }
                                }}
                            >
                                {t('enterprisePage.cta.testDemoButton', 'Try the Audit Tool')}
                            </Button>
                        </Box>
                    </Box>
                </div>

            </main>
        </div>
    );
};

export default EnterprisePage;